import { motion } from 'framer-motion'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#C8923A'
const ACCENT_SOFT = '#E0B870'
const PURPLE = '#8B5E9C'

const AFFILIATE_PROTOCOLO = 'https://affiliate.hotmart.com/affiliate-recruiting/view/5533P105657387'
const AFFILIATE_MAPA = 'https://affiliate.hotmart.com/affiliate-recruiting/view/0427M105026878'
const WHATSAPP_MONICA = 'https://wa.me/5581988671210'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: ACCENT_SOFT }}>
      {children}
    </p>
  )
}

function Divider() {
  return (
    <div
      className="h-px w-full my-8"
      style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}44, transparent)` }}
    />
  )
}

const COMO_FUNCIONA_ITEMS = [
  'Você se cadastra como afiliada na Hotmart.',
  'Recebe um link único personalizado.',
  'Divulga para sua rede usando os materiais que disponibilizamos.',
  'Cada pessoa que comprar pelo seu link gera 50% do valor líquido diretamente na sua conta.',
  'O pagamento é automático e transparente. Você acompanha tudo pelo painel da Hotmart.',
]

const RECEBE_ITEMS = [
  'Acesso ao painel de afiliadas na Hotmart com seu link único personalizado.',
  'Acesso à página de materiais de divulgação com scripts prontos para WhatsApp, Instagram e grupos.',
  'Acesso a criativos prontos para baixar e usar nas suas divulgações.',
]

export default function PageEstrelas() {
  usePageMeta({
    title: 'Seja uma Estrela de Luz — Reative os 4 Elementos',
    description: 'Programa de afiliadas Estrelas de Luz: indique o Protocolo de Liberação e Cocriação e o Mapa de Cura dos 4 Elementos e receba comissões.',
    ogUrl: 'https://jornada.reacendaluz.com.br/estrelas/',
  })

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >

          {/* ── HERO ── */}
          <div className="text-center mb-14">
            {/* Estrela */}
            <motion.div
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.1 }}
              className="flex justify-center mb-7"
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
                <circle cx="28" cy="28" r="24" stroke={ACCENT} strokeWidth="1" opacity="0.4" />
                {/* Triângulo apontando para cima */}
                <polygon
                  points="28,10 43.5,37 12.5,37"
                  fill={ACCENT}
                  opacity="0.85"
                />
                {/* Triângulo apontando para baixo */}
                <polygon
                  points="28,46 12.5,19 43.5,19"
                  fill={ACCENT}
                  opacity="0.85"
                />
              </svg>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
              style={{ color: ACCENT_SOFT }}
            >
              Programa de Afiliadas
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-5"
            >
              Seja uma{' '}
              <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                Estrela de Luz
              </em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="font-display italic text-[18px] md:text-[20px] mb-8 max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'rgba(232,224,213,0.7)' }}
            >
              Você se transformou. Agora propague essa luz para que outras pessoas também possam se transformar e ainda crie uma renda crescente fazendo isso.
            </motion.p>

            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 80, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.55 }}
              className="h-px mx-auto"
              style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}88, transparent)` }}
            />
          </div>

          {/* ── BLOCO 1 — O que é o programa ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}10 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}28`,
            }}
          >
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}14, transparent)` }}
            />
            <SectionLabel>O que é o programa</SectionLabel>
            <p className="font-body type-body leading-relaxed mb-5" style={{ color: 'rgba(232,224,213,0.8)' }}>
              O programa <span className="font-medium" style={{ color: 'var(--ink, #e8e0d5)' }}>Estrelas de Luz</span> foi criado para quem viveu a transformação do Ciclo <span className="numerals">1</span> do Protocolo de Liberação e Cocriação e quer compartilhar essa experiência com outras pessoas sendo remunerada por isso.
            </p>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.75)' }}>
              Não é venda. É indicação genuína. Você indica porque acredita. Porque viveu. Porque sabe o que uma meditação personalizada e um Mapa de Revelações podem fazer pelo campo de alguém.
            </p>
          </motion.div>

          {/* ── BLOCO 2 — Como funciona ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>Como funciona</SectionLabel>
            <ul className="space-y-4">
              {COMO_FUNCIONA_ITEMS.map((text, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.75 + i * 0.06 }}
                  className="flex items-start gap-3"
                >
                  <span className="flex-shrink-0 mt-0.5" style={{ color: ACCENT, fontSize: '14px', lineHeight: 1.7 }}>
                    ✦
                  </span>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {text}
                  </p>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* ── BLOCO 3 — Quanto você ganha ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.78 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}0e 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            <div
              className="absolute -bottom-16 -left-16 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}12, transparent)` }}
            />
            <SectionLabel>Quanto você ganha</SectionLabel>

            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div
                className="rounded-sm p-5"
                style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${ACCENT}22` }}
              >
                <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-2" style={{ color: ACCENT_SOFT, opacity: 0.8 }}>
                  Protocolo · Ciclo <span className="numerals">1</span>
                </p>
                <p className="font-display text-[26px] sm:text-[30px] leading-none mb-1" style={{ color: 'var(--ink, #e8e0d5)' }}>
                  <span className="numerals">~ R$133,50</span>
                </p>
                <p className="font-body text-[14px]" style={{ color: 'rgba(232,224,213,0.55)' }}>
                  por venda
                </p>
              </div>

              <div
                className="rounded-sm p-5"
                style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${PURPLE}33` }}
              >
                <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-2" style={{ color: '#c5a5d4', opacity: 0.8 }}>
                  Mapa de Cura · <span className="numerals">4</span> Elementos
                </p>
                <p className="font-display text-[26px] sm:text-[30px] leading-none mb-1" style={{ color: 'var(--ink, #e8e0d5)' }}>
                  <span className="numerals">~ R$65,73</span>
                </p>
                <p className="font-body text-[14px]" style={{ color: 'rgba(232,224,213,0.55)' }}>
                  por venda
                </p>
              </div>
            </div>

            <p className="font-body type-body leading-relaxed text-center" style={{ color: 'rgba(232,224,213,0.75)' }}>
              Sem limite de indicações. Sem prazo de validade.<br />
              <span className="italic" style={{ color: ACCENT_SOFT }}>Quanto mais você propaga a luz, mais sua renda cresce.</span>
            </p>
          </motion.div>

          {/* ── BLOCO 4 — Quem pode participar ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>Quem pode participar</SectionLabel>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
              O programa <span className="font-medium" style={{ color: 'var(--ink, #e8e0d5)' }}>Estrelas de Luz</span> é exclusivo para quem já se inscreveu e viveu o Ciclo <span className="numerals">1</span> do Protocolo de Liberação e Cocriação.
            </p>
            <p className="font-body type-body leading-relaxed mt-3 italic" style={{ color: ACCENT_SOFT, opacity: 0.85 }}>
              Só indica quem experimentou. Só transforma quem acredita no que está compartilhando.
            </p>
          </motion.div>

          {/* ── BLOCO 5 — O que você recebe ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.92 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-10"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>O que você recebe ao se cadastrar</SectionLabel>
            <ul className="space-y-4">
              {RECEBE_ITEMS.map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5" style={{ color: ACCENT, fontSize: '14px', lineHeight: 1.7 }}>
                    ✦
                  </span>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {text}
                  </p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ── BLOCO 6 — CTAs ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}0a 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}14, transparent)` }}
            />

            <div className="text-center mb-8">
              <SectionLabel>Cadastro como afiliada</SectionLabel>
              <p className="font-display italic text-[17px] md:text-[19px] max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(232,224,213,0.75)' }}>
                Escolha o produto que você quer divulgar e clique no botão correspondente. Você pode se cadastrar nos dois.
              </p>
            </div>

            <div className="flex flex-col gap-4 max-w-xl mx-auto">
              {/* Botão 1 — Protocolo (dourado âmbar) */}
              <a
                href={AFFILIATE_PROTOCOLO}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] transition-all duration-300 active:scale-[0.98] hover:brightness-110"
                style={{ background: ACCENT, color: '#ffffff' }}
              >
                Quero ser afiliada do Protocolo de Liberação e Cocriação
              </a>

              {/* Botão 2 — Mapa (roxo suave) */}
              <a
                href={AFFILIATE_MAPA}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] transition-all duration-300 active:scale-[0.98] hover:brightness-110"
                style={{ background: PURPLE, color: '#ffffff' }}
              >
                Quero ser afiliada do Mapa de Cura dos 4 Elementos
              </a>
            </div>

            <Divider />

            <p className="font-body type-body italic text-center mb-6 max-w-xl mx-auto" style={{ color: 'rgba(232,224,213,0.7)' }}>
              Após se cadastrar na Hotmart avise a Mônica pelo WhatsApp para que sua afiliação seja aprovada e você receba acesso aos materiais de divulgação.
            </p>

            {/* Botão 3 — WhatsApp (contorno dourado) */}
            <div className="max-w-xl mx-auto">
              <a
                href={WHATSAPP_MONICA}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-6 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] transition-all duration-300 active:scale-[0.98] hover:bg-[rgba(200,146,58,0.08)]"
                style={{
                  background: 'transparent',
                  color: ACCENT,
                  border: `1.5px solid ${ACCENT}`,
                }}
              >
                Avisar a Mônica que solicitei minha afiliação
              </a>
            </div>


          </motion.div>

        </motion.div>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-8 text-center border-t mt-12"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="font-display italic text-[15px] mb-2" style={{ color: `${ACCENT_SOFT}99` }}>
          Com amor e presença, Mônica
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz
        </p>
      </footer>
    </div>
  )
}
